import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Pencil, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import ImageField from "@/components/ImageField";

const EMPTY = { title: "", description: "", duration: "", price: "", image: "", date: "", is_group: false, available: true, published: true };
const inputCls = "border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] w-full";

export default function LearnTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editFor, setEditFor] = useState(null);
  const [editImage, setEditImage] = useState("");

  const load = () => api.get("/admin/learn/offerings").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/learn/offerings", { ...form, price: Number(form.price) || 0 });
      setForm(EMPTY);
      load();
      toast.success("Offering added");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const save = async (e, id) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.put(`/admin/learn/offerings/${id}`, {
        title: fd.get("title"), description: fd.get("description"), duration: fd.get("duration"),
        price: Number(fd.get("price")) || 0, image: editImage,
        available: fd.get("available") === "on", published: fd.get("published") === "on",
      });
      setEditFor(null);
      load();
      toast.success("Saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this offering?")) return;
    await api.delete(`/admin/learn/offerings/${id}`);
    load();
  };

  return (
    <div data-testid="admin-learn">
      <form onSubmit={add} className="bg-white border border-[#E5E5E2] p-6 mb-8 grid sm:grid-cols-2 gap-3">
        <p className="sm:col-span-2 text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">New Learn Offering</p>
        <input required placeholder="Title * (e.g. Private Photography Lesson)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="learn-add-title" className={inputCls} />
        <input placeholder="Duration (e.g. 2 hours)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}
          data-testid="learn-add-duration" className={inputCls} />
        <input placeholder="Price (e.g. 350)" type="number" min="0" step="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
          data-testid="learn-add-price" className={inputCls} />
        <input placeholder="Fixed date (optional — e.g. Sat, Dec 6 · 10am)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
          data-testid="learn-add-date" className={inputCls} />
        <textarea placeholder="Description — Enter for a new line, two Enters for a new paragraph" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={4} data-testid="learn-add-description" className="sm:col-span-2 border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] resize-y" />
        <div className="sm:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1.5">Photo</p>
          <ImageField value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="learn-add-image" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
          <input type="checkbox" checked={form.is_group} onChange={(e) => setForm({ ...form, is_group: e.target.checked })} className="accent-[#1A1A1A] w-4 h-4" data-testid="learn-add-group" />
          Group class (fixed date)
        </label>
        <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
          <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} className="accent-[#1A1A1A] w-4 h-4" data-testid="learn-add-available" />
          Available to book
        </label>
        <button type="submit" data-testid="learn-add-submit"
          className="sm:col-span-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] transition-colors">
          Add Offering
        </button>
      </form>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-[#7A7A7A] py-10 text-center">No offerings yet. Add one above to show it on the Learn page.</p>}
        {items.map((o) => (
          <div key={o.id} data-testid={`admin-learn-${o.id}`} className="bg-white border border-[#E5E5E2] p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{o.title} {!o.published && <span className="text-xs text-[#7A7A7A]">(hidden)</span>} {!o.available && <span className="text-xs text-amber-600">(unavailable)</span>}</p>
                <p className="text-xs text-[#7A7A7A]">
                  {o.duration || "No duration"} · {o.price ? `$${o.price}` : "No price"}
                </p>
              </div>
              <button onClick={() => { const next = editFor === o.id ? null : o.id; setEditFor(next); if (next) setEditImage(o.image || ""); }} data-testid={`learn-edit-${o.id}`} className="p-2 border border-[#E5E5E2]">
                {editFor === o.id ? <X size={13} /> : <Pencil size={13} />}
              </button>
              <button onClick={() => remove(o.id)} data-testid={`learn-delete-${o.id}`} className="p-2 border border-[#E5E5E2] text-red-500"><Trash2 size={13} /></button>
            </div>

            {editFor === o.id && (
              <form onSubmit={(e) => save(e, o.id)} className="mt-4 border-t border-[#E5E5E2] pt-4 grid sm:grid-cols-2 gap-3" data-testid={`learn-edit-form-${o.id}`}>
                <input name="title" defaultValue={o.title} className={inputCls} />
                <input name="duration" defaultValue={o.duration} placeholder="Duration" className={inputCls} />
                <input name="price" type="number" min="0" step="1" defaultValue={o.price} placeholder="Price" className={inputCls} />
                <input name="date" defaultValue={o.date || ""} placeholder="Fixed date (optional)" className={inputCls} />
                <textarea name="description" defaultValue={o.description} placeholder="Description — Enter for a new line, two Enters for a new paragraph" rows={4} className="sm:col-span-2 border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] resize-y" />
                <div className="sm:col-span-2">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1.5">Photo</p>
                  <ImageField value={editImage} onChange={setEditImage} testId={`learn-image-${o.id}`} />
                </div>
                <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                  <input name="is_group" type="checkbox" defaultChecked={o.is_group} className="accent-[#1A1A1A] w-4 h-4" data-testid={`learn-group-${o.id}`} />
                  Group class (fixed date)
                </label>
                <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                  <input name="available" type="checkbox" defaultChecked={o.available} className="accent-[#1A1A1A] w-4 h-4" data-testid={`learn-available-${o.id}`} />
                  Available to book
                </label>
                <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                  <input name="published" type="checkbox" defaultChecked={o.published} className="accent-[#1A1A1A] w-4 h-4" data-testid={`learn-published-${o.id}`} />
                  Visible on the site
                </label>
                <button type="submit" data-testid={`learn-save-${o.id}`}
                  className="sm:col-span-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-2.5 hover:bg-[#4A4A4A] transition-colors justify-self-start">
                  Save
                </button>
              </form>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
